import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@chakra-ui/react";
import { FaDirections, FaMapMarkedAlt, FaBookOpen } from "react-icons/fa";
import { FloatingNav } from "./FloatingNav";
import { DirectionsDrawer } from "../Drawer/DirectionsDrawer";
import { GuidesMapDrawer } from "../Drawer/GuidesMapDrawer";
import { GuideDrawer } from "../Drawer/GuideDrawer";
import { useAllRoomGuides } from "../Logic/useAllRoomGuides";
import { guidesWithAddresses } from "../Logic/guideLocations";

// The way into everything location-related: Directions, Map, and (on the main
// page) the Info hub. Owns the drawers those open, so a page only has to drop
// this in. Omit `onOpenInfo` to leave the Info button out.
//
// `onOpenChange(true|false)` reports whether any of its drawers is open, so
// the page can hold back things that shouldn't show over an overlay (the door
// tooltip). `onVisibleChange(true|false)` reports whether the bar itself is
// showing.
export const HubNavigation = ({
  room,
  onOpenInfo,
  onOpenChange,
  onVisibleChange,
  reserveSpace = true,
}) => {
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);

  const { guides, loading, fetched } = useAllRoomGuides(room?.id);

  const hasAddress = Boolean(room?.address);
  // The map plots guides by the address in their description. With none to
  // plot it would show only the property — which Directions already covers —
  // so the Map button is left out.
  const hasMapPins = useMemo(() => guidesWithAddresses(guides).length > 0, [guides]);

  const anyOpen = directionsOpen || mapOpen || Boolean(selectedGuide);
  useEffect(() => {
    onOpenChange?.(anyOpen);
  }, [anyOpen, onOpenChange]);

  const handleGuideSelect = useCallback((guide) => setSelectedGuide(guide), []);
  const handleGuideClose = useCallback(() => setSelectedGuide(null), []);

  const items = [
    ...(hasAddress
      ? [
          {
            id: "directions",
            label: "Directions",
            icon: FaDirections,
            primary: true,
            onClick: () => setDirectionsOpen(true),
          },
        ]
      : []),
    ...(hasMapPins
      ? [
          {
            id: "map",
            label: "Map",
            icon: FaMapMarkedAlt,
            onClick: () => setMapOpen(true),
          },
        ]
      : []),
    // The Info page passes no `onOpenInfo` — it's already there, so the bar
    // only offers the other two.
    ...(onOpenInfo
      ? [{ id: "info", label: "Info", icon: FaBookOpen, onClick: onOpenInfo }]
      : []),
  ];

  // A bar holding only "Info" would be clutter — it needs a second action.
  const visible = items.length >= (onOpenInfo ? 2 : 1);
  useEffect(() => {
    onVisibleChange?.(visible);
  }, [visible, onVisibleChange]);

  if (!visible) return null;

  return (
    <>
      {/* Keeps the end of the page clear of the floating bar. A page whose
          last element can take its own padding passes reserveSpace={false}. */}
      {reserveSpace && <Box h="88px" />}

      <FloatingNav items={items} />

      <DirectionsDrawer
        isOpen={directionsOpen}
        onClose={() => setDirectionsOpen(false)}
        room={room}
      />

      <GuidesMapDrawer
        isOpen={mapOpen}
        onClose={() => setMapOpen(false)}
        room={room}
        guides={guides}
        loading={loading && !fetched}
        onGuideSelect={handleGuideSelect}
        // A pin opens its guide on top of the map drawer.
        isCovered={Boolean(selectedGuide)}
      />

      <GuideDrawer
        isOpen={Boolean(selectedGuide)}
        onClose={handleGuideClose}
        guide={selectedGuide}
      />
    </>
  );
};

export default HubNavigation;
